import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GitBranch, Plus, Clock, Repeat, Infinity as InfinityIcon, Sparkles, MoreHorizontal, Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface WorkflowRow {
  id: string;
  name: string | null;
  initial_goal: string | null;
  cadence: string | null;
  phase: 'discovery' | 'executing' | 'finished' | null;
  status: string | null;
  started_at: string;
  completed_at: string | null;
}

interface WorkflowStepRow {
  workflow_conversation_id: string;
  type: 'manual' | 'ai_assisted' | 'other';
  phase: 'proposed' | 'prompt_drafting' | 'prompt_ready' | 'awaiting_output' | 'analyzing' | 'done';
}

interface ListedWorkflow extends WorkflowRow {
  steps: WorkflowStepRow[];
}

const CADENCE_CONFIG: Record<string, { label: string; icon: typeof Clock }> = {
  one_time: { label: 'One-time', icon: Clock },
  recurring: { label: 'Recurring', icon: Repeat },
  perpetual: { label: 'Perpetual', icon: InfinityIcon },
};

const PHASE_CONFIG: Record<NonNullable<WorkflowRow['phase']>, { label: string; className: string }> = {
  discovery: { label: 'In discovery', className: 'bg-muted text-muted-foreground' },
  executing: { label: 'Building', className: 'bg-primary/15 text-primary border-primary/30' },
  finished: { label: 'Finished', className: 'bg-success/15 text-success-foreground border-success/30' },
};

function lastActivity(w: WorkflowRow): string {
  return w.completed_at ?? w.started_at;
}

function WorkflowCard({ workflow, onClick, onArchive }: { workflow: ListedWorkflow; onClick: () => void; onArchive: () => void }) {
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const cadenceCfg = workflow.cadence ? (CADENCE_CONFIG[workflow.cadence] ?? null) : null;
  const phaseCfg = workflow.phase ? PHASE_CONFIG[workflow.phase] : null;
  const totalSteps = workflow.steps.length;
  const aiSteps = workflow.steps.filter(s => s.type === 'ai_assisted').length;
  const doneSteps = workflow.steps.filter(s => s.phase === 'done').length;
  const title = workflow.name || workflow.initial_goal || 'Untitled workflow';
  const subtitle = workflow.initial_goal && workflow.initial_goal !== title ? workflow.initial_goal : '';

  return (
    <>
      <Card
        className="cursor-pointer border border-border/60 hover:border-primary/30 hover:shadow-sm transition-all"
        onClick={onClick}
      >
        <CardContent className="p-4 pt-4 md:pt-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold text-foreground line-clamp-1">{title}</h3>
            <div className="flex items-center gap-1.5 shrink-0">
              {phaseCfg && (
                <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0.5', phaseCfg.className)}>
                  {phaseCfg.label}
                </Badge>
              )}
              {cadenceCfg && (
                <Badge variant="outline" className="gap-1 text-xs">
                  <cadenceCfg.icon className="h-3 w-3" />
                  {cadenceCfg.label}
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowArchiveDialog(true);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {subtitle && (
            <p className="text-sm text-muted-foreground line-clamp-2">{subtitle}</p>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <span>{totalSteps} {totalSteps === 1 ? 'step' : 'steps'}</span>
            {totalSteps > 0 && (
              <>
                <span>·</span>
                <span>{doneSteps}/{totalSteps} done</span>
              </>
            )}
            {aiSteps > 0 && (
              <>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {aiSteps} AI
                </span>
              </>
            )}
            <span>·</span>
            <span>{new Date(lastActivity(workflow)).toLocaleDateString()}</span>
          </div>

          {/* Step status pips */}
          {totalSteps > 0 && (
            <div className="flex gap-1">
              {workflow.steps.slice(0, 8).map((step, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1.5 flex-1 rounded-full max-w-8',
                    step.phase === 'done' ? 'bg-success/70' :
                    step.phase === 'proposed' ? 'bg-muted-foreground/30' :
                    'bg-primary/60',
                  )}
                  title={`${step.stepName} · ${step.phase}`}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the workflow from your list. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const WorkflowPage = () => {
  const { user, membership } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<ListedWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Some pages (e.g. PromptPage) navigate to `/workflows` with
  // `{ openBuilder: true, initialGoal }` from the v1 modal era. In v2 the
  // builder is a dedicated route — forward those launch requests straight
  // through to `/workflows/new` with the initial goal preserved.
  const launchState = location.state as { initialGoal?: string; openBuilder?: boolean } | null;
  useEffect(() => {
    if (launchState?.openBuilder) {
      navigate('/workflows/new', {
        replace: true,
        state: launchState.initialGoal ? { initialGoal: launchState.initialGoal } : undefined,
      });
    }
  }, [launchState, navigate]);

  const fetchWorkflows = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      // 1. Fetch the user's workflow_conversations. Skip 'abandoned' and
      //    archived rows; show anything in-progress or finished.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wfRes: { data: WorkflowRow[] | null; error: { message?: string } | null } = await (supabase as any)
        .from('workflow_conversations')
        .select('id, name, initial_goal, cadence, phase, status, started_at, completed_at')
        .eq('user_id', user.id)
        .neq('status', 'abandoned')
        .eq('is_archived', false)
        .order('started_at', { ascending: false });

      if (wfRes.error) {
        console.error('[WorkflowPage] fetch error:', wfRes.error);
        setWorkflows([]);
        return;
      }

      const rows = (wfRes.data ?? []) as WorkflowRow[];
      if (rows.length === 0) {
        setWorkflows([]);
        return;
      }

      // 2. Fetch steps for all workflows in a single query, group client-side.
      const ids = rows.map(r => r.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stepsRes: any = await (supabase as any)
        .from('workflow_steps')
        .select('workflow_conversation_id, type, phase')
        .in('workflow_conversation_id', ids);

      const stepsByWf = new Map<string, WorkflowStepRow[]>();
      if (!stepsRes.error && Array.isArray(stepsRes.data)) {
        for (const s of stepsRes.data as WorkflowStepRow[]) {
          const arr = stepsByWf.get(s.workflow_conversation_id) ?? [];
          arr.push(s);
          stepsByWf.set(s.workflow_conversation_id, arr);
        }
      }

      // 3. Hide rows that are still empty discoveries (no name, no objective,
      //    no steps yet). These are auto-created drafts from "Build Workflow"
      //    that the user abandoned before sending a single message.
      const visible = rows
        .map(r => ({ ...r, steps: stepsByWf.get(r.id) ?? [] }))
        .filter(w => {
          const hasContent = (w.name && w.name.trim()) || (w.initial_goal && w.initial_goal.trim());
          return hasContent || w.steps.length > 0 || w.phase !== 'discovery';
        });

      visible.sort((a, b) => new Date(lastActivity(b)).getTime() - new Date(lastActivity(a)).getTime());
      setWorkflows(visible);
    } catch (err) {
      console.error('[WorkflowPage] fetch error:', err);
      setWorkflows([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleBuildWorkflow = () => {
    navigate('/workflows/new');
  };

  const handleViewWorkflow = (workflow: ListedWorkflow) => {
    navigate(`/workflows/${workflow.id}`);
  };

  const handleArchiveWorkflow = async (workflowId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('workflow_conversations')
      .update({ is_archived: true })
      .eq('id', workflowId);

    if (error) {
      console.error('[WorkflowPage] archive error:', error);
      toast.error('Failed to archive workflow');
      return;
    }
    toast.success('Workflow archived');
    setWorkflows(prev => prev.filter(w => w.id !== workflowId));
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold">Workflows</h1>
              <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary">Beta</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your workflows — in progress and finished.
            </p>
          </div>
          <Button onClick={handleBuildWorkflow} className="gap-2">
            <Plus className="h-4 w-4" />
            Build Workflow
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        ) : workflows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <GitBranch className="h-8 w-8 text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-lg font-semibold">No workflows yet</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Build your first workflow to break down a complex objective into actionable steps with the right AI tools at each stage.
              </p>
            </div>
            <Button onClick={handleBuildWorkflow} className="gap-2">
              <Plus className="h-4 w-4" />
              Build Your First Workflow
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflows.map(w => (
              <WorkflowCard
                key={w.id}
                workflow={w}
                onClick={() => handleViewWorkflow(w)}
                onArchive={() => handleArchiveWorkflow(w.id)}
              />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WorkflowPage;
